import React, { useEffect, useState } from "react";
import { Loader2, TrendingUp, Calendar, PhoneCall, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import { useBranding, withAlpha } from "../../lib/branding";

export default function PortalPerformance() {
  const branding = useBranding();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const accent = branding?.accent_color || "#FFB000";

  useEffect(() => {
    api.get("/portal/performance").then(({ data }) => {
      setStats(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-white/50 py-12 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  if (!stats) return <div className="text-white/50 py-12">Couldn't load performance.</div>;

  const fmt = (n) => (n == null ? "—" : n.toLocaleString());

  return (
    <div className="space-y-8" data-testid="portal-performance-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-medium mb-2" style={{ color: accent }}>Performance</div>
        <h1 className="text-3xl font-bold tracking-tight">How your AI is doing</h1>
        <p className="text-sm text-white/50 mt-2">The numbers that matter — calls, bookings, conversion.</p>
      </div>

      {/* Big rates */}
      <div className="grid md:grid-cols-2 gap-4">
        <RateCard
          icon={CheckCircle2}
          label="Booking rate"
          value={`${stats.booking_rate_per_100}%`}
          sub={`${fmt(stats.booked)} booked / ${fmt(stats.total_calls)} calls`}
          accent={accent}
        />
        <RateCard
          icon={PhoneCall}
          label="Answer rate"
          value={`${stats.answer_rate_per_100}%`}
          sub={`${fmt(stats.answered)} connected / ${fmt(stats.total_calls)} dialed`}
          accent={accent}
        />
      </div>

      {/* Volume */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Calls today" value={fmt(stats.calls_today)} accent={accent} />
        <Stat label="This week" value={fmt(stats.calls_week)} accent={accent} />
        <Stat label="This month" value={fmt(stats.calls_month)} accent={accent} />
        <Stat label="All time" value={fmt(stats.total_calls)} accent={accent} />
      </div>

      {/* Outcomes breakdown */}
      <div className="bg-[#121214] border border-white/5 rounded-2xl p-6" data-testid="portal-outcomes-card">
        <h2 className="text-lg font-semibold mb-4">Call outcomes</h2>
        <div className="space-y-3">
          <Bar label="Booked" value={stats.booked} total={stats.total_calls || 1} color={accent} />
          <Bar label="Hot leads" value={stats.hot_leads} total={stats.total_calls || 1} color="#F59E0B" />
          <Bar label="Got email" value={stats.got_email} total={stats.total_calls || 1} color="#0EA5E9" />
        </div>
      </div>

      {/* Appointments breakdown */}
      <div className="bg-[#121214] border border-white/5 rounded-2xl p-6" data-testid="portal-appts-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Appointments</h2>
          <Calendar className="w-4 h-4" style={{ color: accent }} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniStat label="Total" value={stats.appointments_total} />
          <MiniStat label="Upcoming" value={stats.appointments_upcoming} accent={accent} />
          <MiniStat label="Completed" value={stats.appointments_completed} />
          <MiniStat label="No-shows" value={stats.appointments_no_show} />
        </div>
      </div>
    </div>
  );
}

function RateCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div
      className="bg-[#121214] rounded-2xl p-6 border"
      style={{ borderColor: withAlpha(accent, 0.2) }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.12em] text-white/45">{label}</div>
        <Icon className="w-4 h-4" style={{ color: accent }} strokeWidth={1.75} />
      </div>
      <div className="text-4xl font-bold tracking-tight" style={{ color: accent }}>{value}</div>
      <div className="text-xs text-white/45 mt-2">{sub}</div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-[#121214] border border-white/5 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-[0.12em] text-white/40 mb-1.5">{label}</div>
      <div className="text-2xl font-bold" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div className="bg-black/20 border border-white/5 rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-[0.1em] text-white/40">{label}</div>
      <div className="text-xl font-bold mt-1" style={{ color: accent || "#fff" }}>{value}</div>
    </div>
  );
}

function Bar({ label, value, total, color }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="text-white/65 font-medium">{label}</span>
        <span className="text-white/45">{value} <span className="text-white/30">({pct}%)</span></span>
      </div>
      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
}
